import './iterable-form.css';

import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import classNames from 'classnames';
import { Script } from 'gatsby';
import React, { FormEvent, useState } from 'react';

import * as styles from './iterableForm.module.css';

const RECAPTCHA_SITE_KEY = process.env.GATSBY_RECAPTCHA_V3_SITE_KEY;

interface IterableFormProps {
	className: string;
	iterableForm: any;
	setFormSubmitted: (value: boolean) => void;
}

export function IterableForm({
	className,
	iterableForm,
	setFormSubmitted,
}: IterableFormProps) {
	// state
	const [formStarted, setFormStarted] = useState(false);
	const [recaptchaError, setRecaptchaError] = useState(false);

	const handleIterableFormSubmit = async (
		event: FormEvent<HTMLFormElement>
	) => {
		event.preventDefault();
		setRecaptchaError(false);

		try {
			const token = await new Promise((resolve) => {
				grecaptcha.ready(() => {
					grecaptcha
						.execute(RECAPTCHA_SITE_KEY, {
							action: 'submit',
						})
						.then(resolve);
				});
			});

			// Verify the token with our Netlify function
			const verifyResponse = await fetch(
				'/.netlify/functions/verifyRecaptcha',
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ token }),
				}
			);

			const { success, score } = await verifyResponse.json();

			console.log('Verify Response: ', {
				success,
				score,
			});

			if (!success || score < 0.5) {
				setRecaptchaError(true);
				return;
			}

			// Continue with form submission if score is good
			const fields = new FormData(event.target);
			const object = {};
			fields.forEach((value, key) => (object[key] = value));
			const json = JSON.stringify(object);

			await fetch(
				`/.netlify/functions/iterableForm?listId=${iterableForm.listId}`,
				{
					method: 'POST',
					body: json,
				}
			);

			if (setFormSubmitted) {
				setFormSubmitted(true);
			}
			if (iterableForm?.onSuccess) {
				iterableForm.onSuccess();
			}

			trackSelfDescribingEvent({
				event: {
					schema: 'iglu:com.snowplowanalytics.snowplow/submit_form/jsonschema/1-0-0',
					data: {
						formId: iterableForm.listId,
						firstName: object.iterable_firstName,
						lastName: object.iterable_lastName,
					},
				},
			});

			if (
				iterableForm?.analyticsFormSubmitga4Event?.name &&
				window.gtag
			) {
				window.gtag(
					'event',
					iterableForm?.analyticsFormSubmitga4Event.name,
					{
						send_to: 'G-R0Z6J0E1GE',
					}
				);
			}
		} catch (error) {
			console.error('reCAPTCHA error:', error);
			setRecaptchaError(true);
		}
	};

	const handleFormStart = (event: FormEvent) => {
		if (formStarted) return;
		if (!iterableForm?.analyticsFormStartga4Event?.name) return;

		// Make sure we only run tracking event once
		setFormStarted(true);

		if (window?.gtag) {
			window.gtag(
				'event',
				iterableForm?.analyticsFormStartga4Event.name,
				{
					send_to: 'G-R0Z6J0E1GE',
				}
			);
		}

		event.currentTarget.removeEventListener('input', handleFormStart);
	};

	return (
		<div>
			<Script
				src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`}
			/>
			<form
				className={classNames(className, 'iterableFormContainer')}
				onInput={handleFormStart}
				onSubmit={handleIterableFormSubmit}
			>
				<div className='iterableFormRow'>
					<div className='iterableInput'>
						<label className='iterableLabel'>
							First Name<span>*</span>
						</label>
						<input
							type='text'
							id='iterable_firstName'
							name='iterable_firstName'
							required
							placeholder='First Name'
						/>
					</div>
					<div className='iterableInput'>
						<label className='iterableLabel'>
							Last Name<span>*</span>
						</label>
						<input
							type='text'
							id='iterable_lastName'
							name='iterable_lastName'
							required
							placeholder='Last Name'
						/>
					</div>
				</div>
				<div className='iterableFullFormRow'>
					<div className='iterableInput'>
						<label className='iterableLabel'>
							Email Address<span>*</span>
						</label>
						<input
							type='text'
							id='iterable_email'
							name='iterable_email'
							required
							placeholder='Email'
						/>
					</div>
				</div>
				<div className='iterableFormRow'>
					<div className='iterableFormColumn'>
						<fieldset>
							<legend>Are you new to therapy?</legend>
							<div className='iterableFormRadio'>
								<div className='radioContainer'>
									<input
										type='radio'
										id='yes'
										name='iterable_newToTherapy'
										value='yes'
										required
									/>
									<label htmlFor='yes'>Yes</label>
								</div>
								<div className='radioContainer'>
									<input
										type='radio'
										id='no'
										name='iterable_newToTherapy'
										value='no'
										required
									/>
									<label htmlFor='no'>No</label>
								</div>
							</div>
						</fieldset>
					</div>
				</div>
				<div className='iterableButtonRow'>
					<div>
						<button className='iterableButton' type='submit'>
							Submit
						</button>
						{recaptchaError && (
							<div className={styles.recaptchaError}>
								Please try again
							</div>
						)}
					</div>
				</div>
			</form>
		</div>
	);
}
